import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, TrendingUp, ArrowRight } from 'lucide-react';
import { urlFor } from '../../lib/sanity';
import type { Post } from '../../types/sanity';

interface BlogSidebarProps {
  recentPosts: Post[];
  categories: string[];
  onCategorySelect: (category: string) => void;
}

export function BlogSidebar({ recentPosts, categories, onCategorySelect }: BlogSidebarProps) {
  return (
    <aside className="space-y-8">
      {/* Recent Posts */}
      <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
        <h2 className="heading-4 mb-4 text-accessible-gray-900 dark:text-white high-contrast:text-white">
          Ostatnie wpisy
        </h2>
        <div className="space-y-4">
          {recentPosts.map((post) => (
            <Link
              key={post._id}
              to={`/aktualnosci/${post.slug.current}`}
              className="flex gap-3 group"
            >
              {post.mainImage && (
                <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
                  <img
                    src={urlFor(post.mainImage).width(80).height(80).url()}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <div>
                <h3 className="text-sm font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white line-clamp-2 group-hover:text-accessible-red-600 transition-colors">
                  {post.title}
                </h3>
                <div className="flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white/70" />
                  <span className="text-xs text-accessible-gray-600 dark:text-white/90 high-contrast:text-white/70">
                    {new Date(post.publishedAt).toLocaleDateString('pl-PL')}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      
      {/* Categories */}
      <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
        <h2 className="heading-4 mb-4 text-accessible-gray-900 dark:text-white high-contrast:text-white">
          Kategorie
        </h2>
        <div className="space-y-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => onCategorySelect(category)}
              className="flex items-start justify-between w-full p-2 rounded-lg text-left hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10 transition-colors group"
            >
              <div className="flex items-start gap-2 min-w-0">
                <Tag className="w-4 h-4 mt-0.5 flex-shrink-0 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white group-hover:text-accessible-red-600 transition-colors" />
                <span className="text-accessible-gray-700 dark:text-white high-contrast:text-white group-hover:text-accessible-red-600 transition-colors">
                  {category}
                </span>
              </div>
              <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white group-hover:text-accessible-red-600 transition-colors" />
            </button>
          ))}
        </div>
      </div>
      
      {/* Support Banner */}
      <div className="bg-accessible-red-600 p-6 rounded-lg shadow-lg text-white">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-6 h-6 text-white" />
          <h2 className="text-xl font-bold text-white">
            Wesprzyj nas
          </h2>
        </div>
        <p className="text-white/90 mb-4">
          Twoje wsparcie pomaga nam zapewnić opiekę, leczenie i bezpieczny dom dla bezdomnych zwierząt.
        </p>
        <Link
          to="/wplata-jednorazowa"
          className="inline-flex items-center gap-2 bg-white text-accessible-red-600 px-4 py-2 rounded-lg hover:bg-accessible-gray-100 transition-colors font-medium"
        >
          <span>Wpłać teraz</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </aside>
  );
}